import { type FC, type HTMLAttributes, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Dialog, TriggerButton, useDialog } from "widgets";

import ColumnSelectionButton from "../core/ColumnSelectionButton.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";
import { createSelectionModel } from "@/app-utils/selection-model.ts";
import { type InputTableQuerySpec, type VariantAnalysisGroup } from "@/graphql.ts";
import { setGroupMutantGenomeIds } from "@/redux/modules/saved-group.ts";
import { type ITableData, createUniqueKeyFromTableSpec, getTableDataSlice } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, ORGANISM_TABLE_COLUMNS } from "@/settings/settings.ts";

export interface IVariantMutantGenomesSelectionDialogProps extends IVariantMutantGenomesSelectionWidgetProps {
  close: () => void;
  dialogProps: HTMLAttributes<HTMLElement>;
  underlayProps: HTMLAttributes<HTMLElement>;
}

const VariantMutantGenomesSelectionDialog: FC<IVariantMutantGenomesSelectionDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [initialSelection] = useState<number[]>(
    (props.tableQuerySpec.restrictions?.variantGenomeIds ?? []) as number[],
  );

  const vcfMutantGenomesTableSpec: InputTableQuerySpec = {
    id: props.variantAnalysisGroup?.id,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.ANALYSIS_GROUP,
    resultType: ENTITY_TYPE.ORGANISM,
  };

  const vcfMutantGenomesTableData: ITableData = useSelector<IAppState, ITableData>((state) =>
    getTableDataSlice(state.tableData, vcfMutantGenomesTableSpec),
  );

  const onSave = () => {
    dispatch(setGroupMutantGenomeIds(vcfMutantGenomesTableData.selectionModel.selectedIds as number[]));
    props.close();
  };

  return (
    <Dialog
      title={"Genomes"}
      subtitle={"Select the mutant genomes to show"}
      mainButton={{
        label: "Done",
        onPress: onSave,
      }}
      actions={[
        <ColumnSelectionButton
          portalContext={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
          key={"variant-mutant-genomes-selection-form-action-1"}
        />,
      ]}
      contentStyle={{ padding: 0, height: "100%", position: "relative", borderTop: "1px solid #d8e4ed" }}
      fullscreen={true}
      closeFn={props.close}
      domProps={props.dialogProps}
      underlayProps={props.underlayProps}
    >
      <DataTableContainer
        key={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
        tableSpec={vcfMutantGenomesTableSpec}
        tableInitializationOptions={{
          initialSelectionModel: createSelectionModel(initialSelection),
          sortExpression: ORGANISM_TABLE_COLUMNS.NAME,
          filterExpression: "isReference=='false'",
        }}
      />
    </Dialog>
  );
};

export { VariantMutantGenomesSelectionDialog };

export interface IVariantMutantGenomesSelectionWidgetProps {
  variantAnalysisGroup?: VariantAnalysisGroup;
  tableQuerySpec: InputTableQuerySpec;
}

const VariantMutantGenomesSelectionWidget: FC<IVariantMutantGenomesSelectionWidgetProps> = (props) => {
  const { dialogProps, underlayProps, state } = useDialog<HTMLDivElement>();

  const selectionCount: number = (props.tableQuerySpec.restrictions?.variantGenomeIds ?? []).length;

  return (
    <>
      <TriggerButton
        icon={"visibility"}
        onPress={state.open}
        label={"Genomes"}
        showLabel
        badge={selectionCount || "0"}
      />
      {state.isOpen && (
        <VariantMutantGenomesSelectionDialog
          {...props}
          close={state.close}
          dialogProps={dialogProps}
          underlayProps={underlayProps}
        />
      )}
    </>
  );
};
export { VariantMutantGenomesSelectionWidget };
